/**
 * The manage modal (design §11 Phase 1, screen 3's row action) — one modal, driven entirely by whether
 * the customer already has an open period:
 *
 *  • NO open period → the ONE-SHOT create form: dates + optional sold-at account + the renew decision
 *    (Undecided/Yes/No, and when No, the expiry-action options) → a single POST /periods that sets
 *    everything atomically (r10 22/7 — see the banner further down this file for why).
 *  • an open period → edit its dates (PATCH), the renew decision (POST /renew-decision — ALL THREE
 *    choices now, see the 30/7 note below), and Close.
 *
 * 30/7 (คุณใหม่, two bugs off the prod screen) — "Yes" was a dead end and a trap:
 *   1. The create branch offered it, the server 422'd it ("ใช้ปุ่มต่ออายุ (Renew)"), and that button only
 *      exists once a period does. Yes meant: save → reopen → Yes → dates → Renew.
 *   2. The edit branch's Yes called POST /renew, which closes the current round and opens the next one
 *      immediately — so the row started showing next year's dates on the spot, and the round the customer
 *      was actually in became uneditable (a closed period 409s).
 * Both branches now render the SAME <NextRoundFields/> the moment Yes is picked and save in ONE request
 * carrying `next_period_end`; the server parks the plan and the 00:01 cron promotes it on the day. POST
 * /renew still exists server-side for an EARLY renewal, but this modal no longer routes Yes through it.
 *
 * The renew=false branch requires an expiry action, and grace_2stage additionally requires grace_days >
 * 0 — validated client-side (renewValidation.ts) so the operator sees the problem before a round trip.
 * Both branches reuse the exact same RenewRadio/ExpiryActionRadio pieces and the same `renewChoice`/
 * `expiryAction`/`graceDays` state — safe because only one branch ever mounts per modal instance
 * (`hasPeriod` is fixed for the lifetime of the `user` prop this modal was opened with).
 *
 * rev 9 (21/7, principal decision — managed-domain removal): the unlink-family actions
 * (grace_2stage/disassociate_now) are no longer gated on the account's managed-domain state — every
 * expiry action below is always selectable wherever this form offers one. That decision also removed
 * ManagedDomainBadge.tsx, the has_managed_domain/managed_domains_checked_at fields (api/types.ts), the
 * recheck-domains endpoint, and this modal's own `account` prop — it existed solely to feed that gate,
 * so it's gone along with it. Any 4xx the server still returns is rendered here VERBATIM, exactly like
 * every other refusal in this app.
 *
 * rev 6 R6: the old Status column (active/pending/inactive on Zoom's side) is no longer a table column —
 * it renders here as a read-only info row (never an editable field), reusing the shared `statusTone`
 * helper for its colour (r12 22/7 replaces the LABEL side with pool-specific copy — see
 * ZOOM_STATUS_LABEL_KEY below for why it can't reuse the shared `statusLabel`).
 *
 * r10 (22/7, principal feedback): title is now generic ("Manage — {{email}}", pool.period.title) in BOTH
 * states — this modal does more than periods now, in both the create and the edit branch, so a title
 * that only ever said "period" undersold it. Same for the row button that opens it (PoolUsersPage) — was
 * "Set period", is now "Manage" (pool.users.manageButton).
 *
 * r12 (22/7, principal feedback, prod screenshot): three fixes.
 *  1. Modal size lg → xl, AND Modal.tsx itself now caps the dialog height + scrolls only the body (see
 *     that file's own banner) — the date-range popover opening was forcing the whole thing off-screen
 *     with no way to reach it.
 *  2. Sold-at copy rewritten (was "Sold at account (optional)", easy to misread as "this changes where
 *     the license lives") — new label + a helper line spelling out it's a sale record for LOOKING BACK
 *     only, never consulted by the license-removal path (which always reads the customer's CURRENT
 *     account).
 *  3. The Zoom-status row's label and values get pool-specific copy (see banner above + below) instead of
 *     the shared status.* catalogue, so "pending" reads as the actual thing it means here — an unaccepted
 *     Zoom invite (the kotchaphat case) — not the generic "Pending" invoices/subscriptions use elsewhere.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError } from "@/api/client";
import { Button, DateRangePicker, Input, Modal, useNotifier } from "@/components/ui";
import { CustomerHistoryModal } from "@/features/pool/CustomerHistoryModal";
import { ForceActionDialog, type ForceActionKind } from "@/features/pool/ForceActionDialog";
import {
  ACTION_LABEL_KEY,
  ExpiryActionRadio,
  NextRoundFields,
  RenewDecisionFields,
  RenewRadio,
} from "@/features/pool/RenewDecisionFields";
import { todayIso, oneYearFrom } from "@/features/pool/dateUtils";
import {
  useClosePeriod,
  useCreatePeriod,
  usePoolAccess,
  useRenewDecision,
  useUpdatePeriod,
} from "@/features/pool/hooks";
import {
  buildCreatePeriodBody,
  graceDaysVisible,
  renewChoiceFromFlag,
  validateRenewNo,
  validateRenewYes,
  type RenewChoice,
} from "@/features/pool/renewValidation";
import { POOL_EXPIRY_ACTIONS } from "@/api/types";
import type { PoolExpiryAction, PoolRenewDecisionBody, PoolUser } from "@/api/types";

/** Server 4xx message surfaced VERBATIM (the app-wide refusal rule), falling back to a generic title
 *  only for a non-ApiRequestError (network/unknown). */
function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.error.message : fallback;
}

export interface PeriodModalProps {
  user: PoolUser;
  onClose: () => void;
}

export function PeriodModal({ user, onClose }: PeriodModalProps) {
  const { t } = useTranslation();
  const { notify } = useNotifier();
  const hasPeriod = user.period_id != null;

  const createMut = useCreatePeriod();
  const updateMut = useUpdatePeriod(user.email);
  const renewDecisionMut = useRenewDecision(user.email);
  const closeMut = useClosePeriod(user.email);
  const accessQ = usePoolAccess();
  // Fail-closed while pending/erroring, same as every other `_manage` gate in this module. A pool-Viewer
  // can still open this modal and see everything — only the mutating buttons below are gated (rev 6 R2:
  // period mutations are owner/admin-only; viewer is read-only, not a 403 on this screen).
  const role = accessQ.data?.role;
  const canManage = role === "owner" || role === "admin";
  const readOnlyTitle = canManage ? undefined : t("pool.readOnlyNote");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [forceKind, setForceKind] = useState<ForceActionKind | null>(null);

  // --- "Set period" (create — only rendered when !hasPeriod) ---
  const [newStart, setNewStart] = useState(todayIso());
  const [newEnd, setNewEnd] = useState(oneYearFrom(todayIso()));
  const [createError, setCreateError] = useState<string | null>(null);

  // --- existing period's own dates ---
  const [periodStart, setPeriodStart] = useState(user.period_start ?? todayIso());
  const [periodEnd, setPeriodEnd] = useState(user.period_end ?? oneYearFrom(todayIso()));
  const [periodError, setPeriodError] = useState<string | null>(null);

  // --- renew decision ---
  const [renewChoice, setRenewChoice] = useState<RenewChoice>(renewChoiceFromFlag(user.renew));
  const [expiryAction, setExpiryAction] = useState<PoolExpiryAction>(
    user.expiry_action ?? "license_only",
  );
  const [graceDays, setGraceDays] = useState(
    user.grace_days != null ? String(user.grace_days) : "",
  );
  // 30/7 — the PLANNED next round's end date (its start is derived from the current round's end, see
  // NextRoundFields). Seeded from the server so reopening the modal on a customer who already said Yes
  // shows their real plan instead of an empty box.
  const [nextEnd, setNextEnd] = useState(user.next_period_end ?? "");
  const [renewError, setRenewError] = useState<string | null>(null);

  // --- close period ---
  const [closeReason, setCloseReason] = useState("");
  const [closeError, setCloseError] = useState<string | null>(null);

  /**
   * r10 22/7 (principal: "อย่างอื่นก็ควรทำพร้อมกำหนด period เลย") — ONE save, atomic: dates + sold-at +
   * the renew decision all go in the SAME POST /periods, using the exact renewValidation.ts rule the
   * existing-period branch's "Save decision" button already enforces (validateRenewNo), so a Basic user
   * who's about to become the FIRST period on this account never has to reopen the modal to set renew.
   * "Yes" needs no extra dates here (unlike an existing period's separate renew-now flow) — it is simply
   * the renew flag on the period this very save is creating.
   */
  function submitCreate() {
    setCreateError(null);
    if (renewChoice === "no") {
      const key = validateRenewNo(expiryAction, graceDays);
      if (key) {
        setCreateError(t(key));
        return;
      }
    }
    // 30/7 — a Yes carries its next round in the SAME save (คุณใหม่ bug 1). Validated client-side first
    // so the operator sees the problem without a round trip, exactly like the "No" branch above.
    if (renewChoice === "yes") {
      const key = validateRenewYes(newEnd, nextEnd);
      if (key) {
        setCreateError(t(key));
        return;
      }
    }
    createMut.mutate(
      buildCreatePeriodBody(user.email, {
        start: newStart,
        end: newEnd,
        renewChoice,
        expiryAction,
        graceDays,
        nextEnd,
      }),
      {
        onSuccess: () => {
          notify({ tone: "ok", title: t("pool.period.createSuccess", { email: user.email }) });
          onClose();
        },
        onError: (e) => {
          const status = e instanceof ApiRequestError ? e.error.status : null;
          setCreateError(
            status === 409
              ? t("pool.period.createConflict", { email: user.email })
              : errMessage(e, t("state.errorTitle")),
          );
        },
      },
    );
  }

  function submitPeriodEdit() {
    if (user.period_id == null) return;
    setPeriodError(null);
    updateMut.mutate(
      { periodId: user.period_id, body: { period_start: periodStart, period_end: periodEnd } },
      {
        onSuccess: () => notify({ tone: "ok", title: t("pool.period.updateSuccess") }),
        onError: (e) => setPeriodError(errMessage(e, t("state.errorTitle"))),
      },
    );
  }

  /**
   * ONE submit for all three choices (30/7). It used to be two: "Save decision" for Undecided/No and a
   * separate "Renew" button for Yes that closed the current round on the spot and opened the next — which
   * is precisely why the screen jumped a year ahead (คุณใหม่ bug 2). Yes now goes through the same
   * renew-decision endpoint carrying `next_period_end`; the server parks it and the 00:01 cron promotes it
   * on the day. The one case it acts immediately (current round already expired) comes back as
   * `rolled_forward`, and we say so rather than reporting a plan that isn't a plan anymore.
   */
  function submitRenewDecision() {
    if (user.period_id == null) return;
    setRenewError(null);

    if (renewChoice === "no") {
      const key = validateRenewNo(expiryAction, graceDays);
      if (key) {
        setRenewError(t(key));
        return;
      }
    }
    if (renewChoice === "yes") {
      const key = validateRenewYes(periodEnd, nextEnd);
      if (key) {
        setRenewError(t(key));
        return;
      }
    }

    const body: PoolRenewDecisionBody =
      renewChoice === "no"
        ? {
            renew: false,
            expiry_action: expiryAction,
            ...(graceDaysVisible(expiryAction) ? { grace_days: Number(graceDays) } : {}),
          }
        : renewChoice === "yes"
          ? { renew: true, next_period_end: nextEnd }
          : { renew: null };

    renewDecisionMut.mutate(
      { periodId: user.period_id, body },
      {
        onSuccess: (res) => {
          if (res.rolled_forward) {
            notify({ tone: "ok", title: t("pool.period.renewSuccess", { email: user.email }) });
            onClose();
            return;
          }
          notify({ tone: "ok", title: t("pool.period.decisionSuccess") });
        },
        // Verbatim server message — this is where the 409 managed-domain refusal surfaces.
        onError: (e) => setRenewError(errMessage(e, t("state.errorTitle"))),
      },
    );
  }

  function submitClose() {
    if (user.period_id == null) return;
    setCloseError(null);
    if (!closeReason.trim()) {
      setCloseError(t("pool.period.validation.reasonRequired"));
      return;
    }
    closeMut.mutate(
      { periodId: user.period_id, body: { reason: closeReason.trim() } },
      {
        onSuccess: () => {
          notify({ tone: "ok", title: t("pool.period.closeSuccess", { email: user.email }) });
          onClose();
        },
        onError: (e) => setCloseError(errMessage(e, t("state.errorTitle"))),
      },
    );
  }

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={t("pool.period.title", { email: user.email })}
        size="xl"
        footer={
          hasPeriod ? (
            <Button variant="secondary" onClick={() => setHistoryOpen(true)}>
              {t("pool.period.historyButton")}
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-6">
          {/* แถว "Zoom status" ถูกถอดออกทั้งแถว (คุณใหม่ 22/7: "เอาออก จะทำให้สับสน") — เคสที่มัน
              เคยสื่อถูกคุมด้วยของที่ชัดกว่า: invite ค้าง → conflict alert รายวัน · ควรได้ license →
              badge "ควรได้ license" + email digest (r12) */}
          {!hasPeriod && (
            <section className="flex flex-col gap-5">
              <div className="flex flex-col gap-3">
                <DateRangePicker
                  value={{ start: newStart, end: newEnd }}
                  onChange={(r) => {
                    setNewStart(r.start);
                    setNewEnd(r.end);
                  }}
                  aria-label={`${t("pool.period.startLabel")} / ${t("pool.period.endLabel")}`}
                />
              </div>

              {/* r10 22/7 (principal: "อย่างอื่นก็ควรทำพร้อมกำหนด period เลย") — the renew decision travels
                  WITH creation now instead of needing a reopen. The SAME controlled RenewDecisionFields the
                  bulk Set-period dialog renders, over the same renewChoice/expiryAction/graceDays state —
                  extracted so the two can never drift. "Yes" needs no extra dates here: unlike an existing
                  period's renew-now flow (which closes the old period and opens a fresh one), this save IS
                  the period being created, so "Yes" is just its renew flag. */}
              <RenewDecisionFields
                renewChoice={renewChoice}
                onRenewChoice={setRenewChoice}
                expiryAction={expiryAction}
                onExpiryAction={setExpiryAction}
                graceDays={graceDays}
                onGraceDays={setGraceDays}
                periodEnd={newEnd}
                nextEnd={nextEnd}
                onNextEnd={setNextEnd}
              />

              {createError && (
                <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                  {createError}
                </p>
              )}
              <Button
                loading={createMut.isPending}
                disabled={!canManage}
                title={readOnlyTitle}
                onClick={submitCreate}
                className="self-start"
              >
                {t("pool.period.saveButton")}
              </Button>
            </section>
          )}

          {hasPeriod && (
            <>
              <section className="flex flex-col gap-3 border-b border-(--border-hairline) pb-5">
                <DateRangePicker
                  value={{ start: periodStart, end: periodEnd }}
                  onChange={(r) => {
                    setPeriodStart(r.start);
                    setPeriodEnd(r.end);
                  }}
                  aria-label={`${t("pool.period.startLabel")} / ${t("pool.period.endLabel")}`}
                />
                {periodError && (
                  <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                    {periodError}
                  </p>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  loading={updateMut.isPending}
                  disabled={!canManage}
                  title={readOnlyTitle}
                  onClick={submitPeriodEdit}
                  className="self-start"
                >
                  {t("pool.period.saveButton")}
                </Button>
              </section>

              <section className="flex flex-col gap-3 border-b border-(--border-hairline) pb-5">
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                    {t("pool.period.renewSectionTitle")}
                  </h3>
                  <p className="text-(length:--text-2xs) text-(--text-subtle)">
                    {t("pool.period.renewSectionHint")}
                  </p>
                </div>

                <div
                  role="radiogroup"
                  aria-label={t("pool.period.renewSectionTitle")}
                  className="flex flex-col gap-2"
                >
                  <RenewRadio
                    value="undecided"
                    checked={renewChoice === "undecided"}
                    label={t("pool.period.renewUndecidedLabel")}
                    onSelect={setRenewChoice}
                  />
                  <RenewRadio
                    value="yes"
                    checked={renewChoice === "yes"}
                    label={t("pool.period.renewYesLabel")}
                    onSelect={setRenewChoice}
                  />
                  <RenewRadio
                    value="no"
                    checked={renewChoice === "no"}
                    label={t("pool.period.renewNoLabel")}
                    onSelect={setRenewChoice}
                  />
                </div>

                {/* 30/7 — the SAME sub-form the create branch and the bulk dialog render (one
                    definition, no drift), anchored to THIS period's end date. */}
                {renewChoice === "yes" && (
                  <NextRoundFields periodEnd={periodEnd} nextEnd={nextEnd} onNextEnd={setNextEnd} />
                )}

                {renewChoice === "no" && (
                  <div className="flex flex-col gap-3 rounded-(--radius) border border-(--border) p-3">
                    <div
                      role="radiogroup"
                      aria-label={t("pool.period.expiryActionLabel")}
                      className="flex flex-col gap-2"
                    >
                      <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                        {t("pool.period.expiryActionLabel")}
                      </span>
                      {POOL_EXPIRY_ACTIONS.map((a) => (
                        <ExpiryActionRadio
                          key={a}
                          action={a}
                          checked={expiryAction === a}
                          label={t(ACTION_LABEL_KEY[a])}
                          onSelect={setExpiryAction}
                        />
                      ))}
                    </div>

                    {graceDaysVisible(expiryAction) && (
                      <label className="flex flex-col gap-1.5">
                        <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                          {t("pool.period.graceDaysLabel")}
                        </span>
                        <Input
                          type="number"
                          min={1}
                          value={graceDays}
                          onChange={(e) => setGraceDays(e.target.value)}
                          placeholder={t("pool.period.graceDaysHint")}
                          className="w-32"
                        />
                      </label>
                    )}

                    <Button
                      size="sm"
                      variant="secondary"
                      loading={renewDecisionMut.isPending}
                      disabled={!canManage}
                      title={readOnlyTitle}
                      onClick={submitRenewDecision}
                      className="self-start"
                    >
                      {t("pool.period.saveDecisionButton")}
                    </Button>
                  </div>
                )}

                {/* Yes shares this button with Undecided (30/7): one decision, one save. "No" keeps
                    its own inside the action box, next to the options it depends on. */}
                {renewChoice !== "no" && (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={renewDecisionMut.isPending}
                    disabled={!canManage}
                    title={readOnlyTitle}
                    onClick={submitRenewDecision}
                    className="self-start"
                  >
                    {t("pool.period.saveDecisionButton")}
                  </Button>
                )}

                {renewError && (
                  <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                    {renewError}
                  </p>
                )}
              </section>

              <section className="flex flex-col gap-3">
                <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                  {t("pool.period.closeTitle")}
                </h3>
                <label className="flex flex-col gap-1.5">
                  <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
                    {t("pool.period.closeReasonLabel")}
                  </span>
                  <Input
                    value={closeReason}
                    onChange={(e) => setCloseReason(e.target.value)}
                    placeholder={t("pool.period.closeReasonPlaceholder")}
                  />
                </label>
                {closeError && (
                  <p role="alert" className="text-(length:--text-sm) text-(--danger)">
                    {closeError}
                  </p>
                )}
                <Button
                  variant="danger"
                  size="sm"
                  loading={closeMut.isPending}
                  disabled={!canManage}
                  title={readOnlyTitle}
                  onClick={submitClose}
                  className="self-start"
                >
                  {t("pool.period.closeSubmit")}
                </Button>
              </section>

              <section className="flex flex-col gap-3 border-t border-(--border-hairline) pt-5">
                <div className="flex flex-col gap-0.5">
                  <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
                    {t("pool.period.force.sectionTitle")}
                  </h3>
                  <p className="text-(length:--text-2xs) text-(--text-subtle)">
                    {t("pool.period.force.sectionHint")}
                  </p>
                  {/* rev 15: tell the operator BEFORE the click, not via a skip result after —
                      the buttons stay enabled (a not-ready force is harmless: fail-closed skip
                      + audit) but the outcome is pre-announced. */}
                  {user.mirror_ready === false && (
                    <p className="text-(length:--text-2xs) font-medium text-(--info)" role="note">
                      {t("pool.period.force.mirrorPendingNote")}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={!canManage}
                    title={readOnlyTitle}
                    onClick={() => setForceKind("license-remove")}
                  >
                    {t("pool.period.force.licenseRemoveButton")}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={!canManage}
                    title={readOnlyTitle}
                    onClick={() => setForceKind("unlink")}
                  >
                    {t("pool.period.force.unlinkButton")}
                  </Button>
                </div>
              </section>
            </>
          )}
        </div>
      </Modal>

      {historyOpen && (
        <CustomerHistoryModal email={user.email} onClose={() => setHistoryOpen(false)} />
      )}

      {forceKind && user.period_id != null && (
        <ForceActionDialog
          kind={forceKind}
          periodId={user.period_id}
          email={user.email}
          onClose={() => setForceKind(null)}
        />
      )}
    </>
  );
}
